import { Link } from "react-router-dom";
import { FiArrowUpRight, FiCheck, FiX, FiShield } from "react-icons/fi";

import iconImage from "../../assets/piggy/icon.png";
import Reveal from "./Reveal";
import usePageMeta from "./usePageMeta";
import { FEATURES, NEVER_READS, PIGGY_EMAIL, READS, STEPS } from "./constants";
import "./Piggy.scss";

const DESCRIPTION =
  "Piggy turns the bank alerts already on your phone into a calm, AI-sorted ledger. SMS and Gmail in, one clean row per payment out.";

const Piggy = () => {
  usePageMeta("Piggy — Spending, understood.", DESCRIPTION);

  return (
    <div className="piggy">
      <a className="piggy__skip" href="#piggy-main">
        Skip to content
      </a>

      <header className="piggy__nav">
        <Link to="/piggy" className="piggy__brand" aria-label="Piggy home">
          <img src={iconImage} width={32} height={32} alt="" />
          <span>Piggy</span>
        </Link>
        <nav aria-label="Page">
          <a href="#how">How it works</a>
          <a href="#features">Features</a>
          <a href="#privacy">Privacy</a>
          <Link to="/piggy/privacy">Policy</Link>
        </nav>
      </header>

      <main id="piggy-main">
        {/* ---------------------------------------------------------------- Hero */}
        <section className="piggy__hero" aria-labelledby="hero-title">
          <div className="piggy__hero-copy">
            <p className="piggy__eyebrow piggy__fade" style={{ "--i": 0 }}>
              Android · Early access
            </p>
            <h1 id="hero-title" className="piggy__fade" style={{ "--i": 1 }}>
              Spending,
              <br />
              <em>understood.</em>
            </h1>
            <p className="piggy__lede piggy__fade" style={{ "--i": 2 }}>
              Your bank already tells you about every payment. Piggy listens to
              those alerts, lets AI sort them, and hands you a ledger you can
              actually read. No manual entry. No spreadsheets.
            </p>
            <div className="piggy__cta piggy__fade" style={{ "--i": 3 }}>
              <a
                href={`mailto:${PIGGY_EMAIL}?subject=Piggy%20early%20access`}
                className="piggy__btn piggy__btn--primary"
              >
                Request early access
                <FiArrowUpRight aria-hidden="true" />
              </a>
              <a href="#how" className="piggy__btn piggy__btn--ghost">
                See how it works
              </a>
            </div>
            <p className="piggy__fine piggy__fade" style={{ "--i": 4 }}>
              Built for Indian banks, UPI, and cards. Free while in early access.
            </p>
          </div>

          <div className="piggy__hero-visual piggy__fade" style={{ "--i": 2 }} aria-hidden="true">
            <div className="piggy__stage">
              <div className="piggy__sms">
                <div className="piggy__sms-head">
                  <span className="piggy__sms-sender">VM-HDFCBK</span>
                  <span>now</span>
                </div>
                <p>
                  Rs.<b>640.00</b> debited from a/c **4821 on 20-09-26 to VPA{" "}
                  <b>swiggy@ybl</b>. UPI Ref 526312448809.
                </p>
              </div>

              <div className="piggy__flow">
                <span />
                <span />
                <span />
              </div>

              <div className="piggy__ledger">
                <div className="piggy__ledger-head">
                  <span>Today</span>
                  <span>−₹1,265</span>
                </div>
                <div className="piggy__row piggy__row--new">
                  <span className="piggy__avatar" style={{ "--hue": 18 }}>
                    S
                  </span>
                  <div>
                    <strong>Swiggy</strong>
                    <small>Food &amp; dining · 12:42 pm</small>
                  </div>
                  <span className="piggy__amt piggy__amt--debit">−₹640</span>
                </div>
                <div className="piggy__row">
                  <span className="piggy__avatar" style={{ "--hue": 205 }}>
                    U
                  </span>
                  <div>
                    <strong>Uber</strong>
                    <small>Transport · 9:10 am</small>
                  </div>
                  <span className="piggy__amt piggy__amt--debit">−₹225</span>
                </div>
                <div className="piggy__row">
                  <span className="piggy__avatar" style={{ "--hue": 150 }}>
                    A
                  </span>
                  <div>
                    <strong>Amazon refund</strong>
                    <small>Linked to 14 Sep · 8:02 am</small>
                  </div>
                  <span className="piggy__amt piggy__amt--credit">+₹1,299</span>
                </div>
                <div className="piggy__row">
                  <span className="piggy__avatar" style={{ "--hue": 265 }}>
                    B
                  </span>
                  <div>
                    <strong>BESCOM</strong>
                    <small>Utilities · 7:30 am</small>
                  </div>
                  <span className="piggy__amt piggy__amt--debit">−₹1,400</span>
                </div>
              </div>

              <div className="piggy__chip">
                <span className="piggy__chip-dot" />
                Sorted by AI
              </div>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------- How it works */}
        <section id="how" className="piggy__section piggy__how" aria-labelledby="how-title">
          <Reveal className="piggy__section-head">
            <p className="piggy__eyebrow">How it works</p>
            <h2 id="how-title">Three steps. Zero typing.</h2>
          </Reveal>
          <ol className="piggy__steps">
            {STEPS.map((step, index) => (
              <Reveal as="li" key={step.n} delay={index * 90}>
                <span className="piggy__step-n">{step.n}</span>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </Reveal>
            ))}
          </ol>
        </section>

        {/* -------------------------------------------------------------- Features */}
        <section id="features" className="piggy__section piggy__features" aria-labelledby="features-title">
          <Reveal className="piggy__section-head">
            <p className="piggy__eyebrow">What you get</p>
            <h2 id="features-title">Small app. Careful details.</h2>
            <p className="piggy__section-lede">
              Piggy does one thing: keep an honest record of where money went,
              without asking you to type it in.
            </p>
          </Reveal>
          <ul className="piggy__grid">
            {FEATURES.map(({ icon: Icon, title, copy }, index) => (
              <Reveal as="li" key={title} delay={(index % 3) * 70}>
                <span className="piggy__grid-icon">
                  <Icon aria-hidden="true" />
                </span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </Reveal>
            ))}
          </ul>
        </section>

        {/* --------------------------------------------------------------- Privacy */}
        <section id="privacy" className="piggy__section piggy__privacy" aria-labelledby="privacy-title">
          <Reveal className="piggy__privacy-card">
            <div className="piggy__privacy-copy">
              <p className="piggy__eyebrow">
                <FiShield aria-hidden="true" /> Private by design
              </p>
              <h2 id="privacy-title">Bank alerts only. Never OTPs.</h2>
              <p>
                Piggy reads the fewest messages it can get away with, keeps
                only the transaction details it extracts, and deletes your
                account and data on request. Gmail access is read-only and
                optional.
              </p>
              <div className="piggy__privacy-links">
                <Link to="/piggy/privacy" className="piggy__btn piggy__btn--dark">
                  Read the privacy policy
                </Link>
                <Link to="/piggy/terms" className="piggy__link">
                  Terms of service <FiArrowUpRight aria-hidden="true" />
                </Link>
              </div>
            </div>
            <div className="piggy__privacy-lists">
              <div>
                <h3>
                  <FiCheck aria-hidden="true" /> Piggy reads
                </h3>
                <ul>
                  {READS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3>
                  <FiX aria-hidden="true" /> Piggy never reads
                </h3>
                <ul>
                  {NEVER_READS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="piggy__footer">
        <div className="piggy__footer-brand">
          <img src={iconImage} width={24} height={24} alt="" />
          <span>Piggy</span>
          <span className="piggy__footer-tag">Spending, understood.</span>
        </div>
        <nav aria-label="Footer">
          <Link to="/piggy/privacy">Privacy policy</Link>
          <Link to="/piggy/terms">Terms of service</Link>
          <a href={`mailto:${PIGGY_EMAIL}`}>Contact</a>
        </nav>
        <p className="piggy__footer-copy">
          © {new Date().getFullYear()} Piggy. Not affiliated with any bank,
          card network, or Google.
        </p>
      </footer>
    </div>
  );
};

export default Piggy;
